import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Form, Input, Select, Button, Divider, message } from 'antd';
import { SaveOutlined, CloseOutlined, PlusOutlined, ApartmentOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { departmentsApi } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';

const { Option } = Select;

const AddDepartment = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const response = await departmentsApi.getAll();
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('خطا در دریافت لیست دپارتمان‌ها');
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    try {
      const validatedValues = await form.validateFields();
      setLoading(true);
      const payload = {
        team_name: validatedValues.team_name,
        parent_id: validatedValues.parent_id || null,
      };
      
      await departmentsApi.create(payload);
      message.success('دپارتمان با موفقیت ایجاد شد');
      navigate('/hr/departments');
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else {
        console.error('Error creating department:', error);
        message.error(error?.message || 'خطا در ایجاد دپارتمان');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/hr/departments');
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0 " bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white flex items-center justify-center shadow-lg">
                <PlusOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">افزودن دپارتمان</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ایجاد دپارتمان جدید و تعیین والد (در صورت نیاز)</p>
              </div>
            </div>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={() => navigate('/hr/departments')}
              className="flex items-center gap-2"
              style={{ fontFamily: 'CustomFont' }}
            >
              بازگشت
            </Button>
          </div>

          {/* Form */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="border border-gray-100 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                style={{ fontFamily: 'CustomFont' }}
                size="middle"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    label="نام دپارتمان"
                    name="team_name"
                    rules={[
                      { required: true, message: 'لطفا نام دپارتمان را وارد کنید' },
                      { min: 2, message: 'نام دپارتمان باید حداقل 2 کاراکتر باشد' },
                      { max: 100, message: 'نام دپارتمان نمی‌تواند بیشتر از 100 کاراکتر باشد' }
                    ]}
                  >
                    <Input
                      placeholder="مثال: فروش، بازاریابی، پشتیبانی..."
                      prefix={<ApartmentOutlined className="text-gray-400" />}
                    />
                  </Form.Item>

                  <Form.Item
                    label="دپارتمان والد"
                    name="parent_id"
                    tooltip="در صورت انتخاب، این دپارتمان زیرمجموعه دپارتمان انتخاب‌شده خواهد بود"
                  >
                    <Select
                      placeholder="انتخاب دپارتمان والد (اختیاری)"
                      allowClear
                      showSearch
                      loading={loading}
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {departments.map((dept) => (
                        <Option key={dept.id} value={dept.id}>
                          {dept.team_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <Divider />

                <div className="flex items-center justify-end gap-3">
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancel}
                    style={{ minWidth: '110px' }}
                  >
                    لغو
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 border-0"
                    style={{ minWidth: '140px' }}
                  >
                    ذخیره دپارتمان
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddDepartment;
